/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from 'theme-ui';
import React from 'react';
import * as bannerAds from './images';
import shuffle from 'lodash/shuffle';

const allKeys = Object.keys(bannerAds).filter((key) => !key.startsWith('_'));

type TAdContext = {
  adKeys: typeof allKeys;
  ref: React.MutableRefObject<number>;
};

const AdContext = React.createContext<TAdContext>(undefined);

export function AdProvider({ adKeys = allKeys, children }) {
  const ref = React.useRef(0);
  const shuffledKeys = React.useMemo(() => shuffle(adKeys), [adKeys]);
  return (
    <AdContext.Provider value={{ adKeys: shuffledKeys, ref }}>
      {children}
    </AdContext.Provider>
  );
}

function useNextAd() {
  const context = React.useContext(AdContext);
  if (!context)
    throw new Error(
      'BannerAd was used without an AdContext. Add AdContext to the layout or the page wrapper.'
    );
  const { adKeys, ref } = context;
  const next = React.useMemo(() => {
    const key = adKeys[ref.current];
    ref.current = (ref.current + 1) % adKeys.length;
    return key;
  }, [ref, adKeys]);
  return next;
}

export default function BannerAd(props: JSX.IntrinsicAttributes) {
  const next = useNextAd();

  const CurrentAd = bannerAds[next];
  if (!CurrentAd) return null;
  return <CurrentAd {...props} />;
}
